import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useFloating, offset, flip, shift, autoUpdate, arrow } from "@floating-ui/react";
import { Maximize2 } from "lucide-react";
import { IconClose } from "icons";
import clsx from "clsx";
import { useSiteImage } from "hooks/useSiteImage";
import { helpVideoSlotKey } from "components/Tooltip/helpVideoSlots";

interface ITooltip {
  children: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
  content?: React.ReactNode;
  contentClassName?: string;
  zIndex?: number;
  className?: string;
  active?: boolean;
  /**
   * Optional help clip shown above the text. A muted, looping preview (BE-hosted webm) with an expand
   * button that opens it full-size with a real player + sound. The video card only appears once the clip
   * has actually loaded — so a not-yet-uploaded (404) tool never flashes an empty box, it just stays text.
   * When a video is present the tooltip is interactive: hovering the panel keeps it open so the button is
   * reachable.
   */
  video?: string;
  /**
   * Controlled visibility: when set, it decides alone (hover no longer opens or closes it) — for a tooltip whose
   * trigger is not a DOM child, e.g. a canvas node the caller watches itself (hover delay, not while dragging).
   */
  open?: boolean;
}

// Bridge delay so moving the cursor from the trigger onto the (video) panel doesn't close it.
const CLOSE_DELAY = 120;

type TooltipVideoProps = React.VideoHTMLAttributes<HTMLVideoElement> & { src: string };

const SlottedVideo = ({ slotKey, src, ...props }: TooltipVideoProps & { slotKey: string }) => {
  const resolved = useSiteImage(slotKey, src);

  return <video {...props} src={resolved} />;
};

/**
 * The help clip's `<video>`: a known tool clip plays what backoffice published for its slot (`helpVideoSlots`), its
 * url being the fallback. Only a clip WITH a slot reads the store, so a tooltip without one renders as it always did.
 */
const TooltipVideo = ({ src, ...props }: TooltipVideoProps) => {
  const slotKey = helpVideoSlotKey(src);

  return slotKey ? <SlottedVideo slotKey={slotKey} src={src} {...props} /> : <video {...props} src={src} />;
};

const Tooltip = ({ children, position = "bottom", content = "", contentClassName, zIndex = 100000, className, active = true, video, open: controlledOpen }: ITooltip) => {
  const [open, setOpen] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const arrowRef = useRef<HTMLDivElement | null>(null);

  const { refs, floatingStyles, placement, middlewareData, isPositioned } = useFloating({
    placement: position,
    // 9px clears the pointer's 3px tip outside the panel with a little air to the trigger.
    middleware: [offset(9), flip(), shift({ padding: 8 }), arrow({ element: arrowRef, padding: 10 })],
    whileElementsMounted: autoUpdate,
  });
  // The pointer sits on the side FACING the trigger — the final placement's opposite side, so it follows a flip.
  const arrowSide = ({ top: "bottom", bottom: "top", left: "right", right: "left" } as const)[placement.split("-")[0] as "top" | "bottom" | "left" | "right"];

  // New source → re-attempt load from scratch.
  useEffect(() => {
    setVideoFailed(false);
    setVideoReady(false);
  }, [video]);
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );

  const hasVideo = !!video && !videoFailed; // still attempting / loaded
  const showVideoCard = hasVideo && videoReady; // loaded → drives layout + expand button
  const interactive = hasVideo; // video tooltips stay open while hovered/clicked

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY);
  };

  // Escape closes the expanded overlay.
  useEffect(() => {
    if (!expanded) return;

    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setExpanded(false);
    document.addEventListener("keydown", onKey);

    return () => document.removeEventListener("keydown", onKey);
  }, [expanded]);

  const shouldShow = active && (controlledOpen ?? open);

  return (
    <div
      className={clsx("inline-block relative", className)}
      ref={refs.setReference}
      onMouseEnter={() => {
        if (!active) return;
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={() => {
        if (!active) return;
        if (interactive) scheduleClose();
        else setOpen(false);
      }}
      onClick={() => setOpen(false)}
    >
      {children}
      {shouldShow &&
        (content !== "" || hasVideo) &&
        createPortal(
          <div
            ref={refs.setFloating}
            // Hidden until placed (2026-10-22): its first frame stood at the page's top-left, unmeasured — a flash of a
            // big panel in the corner before it moved to its trigger.
            style={{ ...floatingStyles, zIndex, visibility: isPositioned ? "visible" : "hidden" }}
            onMouseEnter={interactive ? cancelClose : undefined}
            onMouseLeave={interactive ? scheduleClose : undefined}
            className={clsx(
              // Inverted against the page (REF-6350): black on the light theme, white on the dark one. `bg-white` /
              // `text-black` are the THEME tokens, which flip with `[data-theme]` — i.e. trueBlack / trueWhite in
              // light and the reverse in dark — so one class pair covers both themes.
              "relative bg-white text-black text-[13px] font-normal leading-snug rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.08)] transition-opacity duration-100",
              // `w-max` only for text: beside a video card's own width two width classes competed, the stylesheet's order
              // won, and the card took the clip's natural width — a 1000px+ flash (2026-10-22).
              !hasVideo && "w-max",
              // Video-card sizing applies as soon as a video is present (a skeleton fills the frame while it
              // streams), so the tooltip doesn't jump from text-size to card-size when the clip arrives.
              // Under 1800px (xl) a smaller card (2026-10-22): beside the canvas's narrower edit panel the 336px card covered
              // the design it explains. One fixed width there, the video filling it, so the text wraps to the video's edge.
              hasVideo
                ? "flex flex-col gap-1.5 p-1.5 w-[260px] text-[12px] xl:w-auto xl:gap-2 xl:p-2 xl:min-w-[336px] xl:max-w-[380px] xl:text-[14px]"
                : // A video tooltip whose clip failed keeps its card's measure: its text is the long help line, which on
                // one line ran the width of the screen.
                video
                ? "px-3 py-2 max-w-[260px] whitespace-normal xl:max-w-[320px]"
                : "px-3 py-2 whitespace-nowrap",
              contentClassName
            )}
          >
            {/* Pointer toward the trigger. Its colour is INHERITED from the panel, so a caller that restyles the panel
                through `contentClassName` (e.g. `!bg-popover`) gets a matching pointer. Inline, not `bg-inherit`: this
                repo's Tailwind `colors` replaces the defaults and has no `inherit`, so that class is never generated. */}
            <div
              ref={arrowRef}
              aria-hidden
              className="pointer-events-none absolute h-2.5 w-2.5 rotate-45 rounded-[2px]"
              style={{
                backgroundColor: "inherit",
                left: middlewareData.arrow?.x != null ? `${middlewareData.arrow.x}px` : "",
                top: middlewareData.arrow?.y != null ? `${middlewareData.arrow.y}px` : "",
                // Only the tip shows: most of the rotated square tucks under the panel.
                [arrowSide]: "-3px",
              }}
            />
            {hasVideo && (
              <div className="relative">
                <TooltipVideo
                  src={video ?? ""}
                  className="w-full xl:w-[320px] max-w-full aspect-video rounded object-cover bg-black-200"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  onLoadedData={() => setVideoReady(true)}
                  onError={() => setVideoFailed(true)}
                />
                {/* Skeleton over the frame while the clip streams from the CDN — removed once the first frame is ready. */}
                {!videoReady && <div className="absolute inset-0 rounded bg-black-200 animate-pulse" />}
                {showVideoCard && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpanded(true);
                    }}
                    className="absolute top-1.5 right-1.5 flex items-center justify-center h-7 w-7 rounded-full bg-trueBlack/60 text-trueWhite hover:bg-trueBlack/80 transition-colors"
                    aria-label="Expand video"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
            {content !== "" && content}
          </div>,
          document.body
        )}
      {expanded &&
        showVideoCard &&
        createPortal(
          <div className="fixed inset-0 z-[100001] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setExpanded(false)}>
            {/* Close X — copied 1:1 from the Trends/Image-detail overlay (ImageDetailPage): top-left, round. */}
            <button
              onClick={() => setExpanded(false)}
              aria-label="Close"
              className="absolute top-5 left-5 z-10 flex-center h-11 w-11 rounded-full text-grey hover:bg-black-200 hover:text-trueBlack dark:hover:text-white transition-colors"
            >
              <IconClose className="w-6 h-6" />
            </button>
            <div className="relative w-[min(90vw,900px)]" onClick={(e) => e.stopPropagation()}>
              {/* Full player: real controls + sound on. autoPlay is allowed here because the expand click is a user gesture. */}
              <TooltipVideo src={video ?? ""} className="w-full rounded-lg bg-black" controls autoPlay loop playsInline />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Tooltip;
